/*
Not to be confused for comment-macro-extension-handler.tsx in the same package which is used specifically
for the comment editor. This MacroExtensionHandler file is used for creating extension handlers
for next/full-page-editor
*/
import React, { Fragment } from 'react';

import type { ExtensionHandler, ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { EditorActions } from '@atlaskit/editor-core';

import { expValEquals } from '@confluence/feature-experiments';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import {
	getMacroAttributesFromADFNode,
	EDITOR,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { resolveQuestionsMacroExtensionKey } from '@confluence/fabric-extension-lib/entry-points/macroKeyResolver.ts';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features/entry-points/SubscriptionHandlerFnType.ts';
import { PagePropertiesReportRenderer } from '@confluence/page-properties-report-renderer';
import { UserProfileMacroHandler } from '@confluence/profile-macros/entry-points/UserProfileMacroRenderer.ts';
import { ProfilePictureMacroHandler } from '@confluence/profile-macros/entry-points/ProfilePictureMacroRenderer.ts';
import { ContributorsMacroHandler } from '@confluence/contributors-macro/entry-points/ContributorsMacroRenderer.ts';
import { WidgetMacroHandler } from '@confluence/widget-macro/entry-points/WidgetMacroRenderer.ts';
import { ContentByLabelMacroHandler } from '@confluence/content-by-label-macro/entry-points/ContentByLabelMacroRenderer.ts';
import { PageTreeSearchMacroHandler } from '@confluence/pagetree-search-macro/entry-points/PageTreeSearchMacroRenderer.ts';
import { GlobalQuestionsListMacroHandler } from '@confluence/questions/entry-points/GlobalQuestionsListMacroHandler.ts';
import { LegacyMacroRenderer } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererQuery.ts';
import { RecentlyUpdatedMacroHandler } from '@confluence/recently-updated-macro/entry-points/RecentlyUpdatedMacroRenderer.ts';
import { ContentReportTableMacroHandler } from '@confluence/content-report-table-macro/entry-points/ContentReportTableMacroRenderer.ts';

import { AnchorMacroRenderer } from './AnchorMacroRenderer.tsx';
import { DeadMacroRenderer } from './DeadMacroRenderer/index.ts';
import { CreateFromTemplate } from './CreateFromTemplate/index.ts';
import { getMigrationExtHandlerForEditor } from './MigrationExtension/index.ts';
import { ExcerptIncludeExtensionHandler } from './ExcerptInclude/index.ts';
import { BlogPostsExtensionHandler } from './BlogPosts/BlogPostsExtensionHandler.tsx';
import { PageIncludeExtensionHandler } from './PageInclude/PageIncludeExtensionHandler.tsx';
import { TableOfContentsLiveComponent } from './TableOfContentsLiveComponent.tsx';
import { LiveSearchHandler } from './LiveSearch/LiveSearchHandler.tsx';

type Options = {
	contentId: string;
	spaceKey: string;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	editorActions?: EditorActions;
	subscribeDocChange: SubscriptionHandlerFnType;
	unsubscribeDocChange: SubscriptionHandlerFnType;
	isLivePage?: boolean;
	isCollabEditForLiveUpdatesEnabled?: boolean;
};

export const getFullPageExtensionHandlers = ({
	contentId,
	spaceKey,
	extensionContextProvider,
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
	isCollabEditForLiveUpdatesEnabled,
}: Options): ExtensionHandlers => {
	return {
		[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandlerForEditor({
			contentId,
			spaceKey,
			extensionContextProvider,
			editorActions,
			subscribeDocChange,
			unsubscribeDocChange,
			isLivePage,
			isCollabEditForLiveUpdatesEnabled,
		}),
		[EXTENSION_TYPE.MIGRATION]: getMigrationExtHandlerForEditor({
			contentId,
			contextProviders: {}, // we do not yet need ClassicEditorContextProvider for InvalidExtension in next editor
		}),
	};
};

export const getMacroExtensionHandlerForEditor = ({
	contentId,
	spaceKey,
	extensionContextProvider = {
		ClassicEditorContextProvider: ({
			children,
			isWithinEditor: _isWithinEditor,
		}: {
			children: React.ReactNode;
			isWithinEditor?: boolean;
		}) => <>{children}</>,
	},
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
	isCollabEditForLiveUpdatesEnabled,
}): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension, doc) => {
		const resolvedExtensionKey = resolveQuestionsMacroExtensionKey(extension.extensionKey);
		const extensionKey = resolvedExtensionKey;

		// Update extension if key was resolved to a different value
		if (resolvedExtensionKey !== extension.extensionKey) {
			extension = { ...extension, extensionKey: resolvedExtensionKey };
		}

		const attributes = getMacroAttributesFromADFNode(extension);
		const { ClassicEditorContextProvider } = extensionContextProvider;

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			references: doc?.[0]?.references || [],
			contentId,
			spaceKey,
			mode: EDITOR,
			extensionKey,
			attributes,
			multiBodiedExtensionActions: doc?.[0]?.actions,
		};

		let implementation;

		const legacyMacroRenderer = (
			<ClassicEditorContextProvider>
				<LegacyMacroRenderer {...macroDefaultProps} />
			</ClassicEditorContextProvider>
		);

		switch (extensionKey) {
			// blocklist - block extensions - we want to show the editor's placeholder frame, so return empty div
			case 'toc': // toc needs to be rewritten as it accesses the DOM to create its tree
				implementation = (
					<TableOfContentsLiveComponent
						macroDefaultProps={macroDefaultProps}
						editorActions={editorActions}
						subscribeDocChange={subscribeDocChange}
						unsubscribeDocChange={unsubscribeDocChange}
						isCollabEditForLiveUpdatesEnabled={!!isCollabEditForLiveUpdatesEnabled}
					/>
				);
				break;

			case 'create-from-template':
				implementation = <CreateFromTemplate {...macroDefaultProps} />;
				break;

			// blocklist - inline extensions - the editor doesn't render a placeholder frame, so we need to display something
			case 'pagetree': // pagetree is not spa friendly and will only work sometimes
				implementation = <DeadMacroRenderer {...macroDefaultProps} />;
				break;

			case 'anchor':
				implementation = <AnchorMacroRenderer {...macroDefaultProps} />;
				break;

			case 'excerpt-include':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<ExcerptIncludeExtensionHandler
							{...macroDefaultProps}
							isLivePage={isLivePage}
							Renderer={PageContentRenderer}
						/>
					</ClassicEditorContextProvider>
				);
				break;

			case 'detailssummary':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<PagePropertiesReportRenderer
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
							openAnchorsInNewTab
						/>
					</ClassicEditorContextProvider>
				);
				break;
			case 'blog-posts':
				if (expValEquals('cc_rewrite_blog_posts_legacy_macro', 'isEnabled', true)) {
					implementation = (
						<ClassicEditorContextProvider isWithinEditor>
							<BlogPostsExtensionHandler
								{...macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								Renderer={PageContentRenderer}
								appearance="full-page"
							/>
						</ClassicEditorContextProvider>
					);
				} else {
					implementation = legacyMacroRenderer;
				}
				break;
			case 'recently-updated':
				if (expValEquals('cc_rewrite_recently_updated_legacy_macro', 'isEnabled', true)) {
					implementation = (
						<RecentlyUpdatedMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={{
								...extension?.parameters?.macroParams,
								currentSpaceKey: spaceKey,
							}}
						/>
					);
				} else {
					implementation = legacyMacroRenderer;
				}
				break;
			case 'profile-picture':
				implementation = (
					<ProfilePictureMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'profile':
				implementation = (
					<UserProfileMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'include':
				if (expValEquals('cc_rewrite_page_include_legacy_macro', 'isEnabled', true)) {
					implementation = (
						<ClassicEditorContextProvider isWithinEditor>
							<PageIncludeExtensionHandler
								{...macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								Renderer={PageContentRenderer}
								appearance="full-page"
							/>
						</ClassicEditorContextProvider>
					);
				} else {
					implementation = (
						<ClassicEditorContextProvider isWithinEditor>
							<LegacyMacroRenderer {...macroDefaultProps} />
						</ClassicEditorContextProvider>
					);
				}
				break;
			case 'livesearch':
				implementation = <LiveSearchHandler macroDefaultProps={macroDefaultProps} />;
				break;
			case 'questionslist-macro':
				implementation = legacyMacroRenderer;
				break;
			case 'questionslist-macro-native':
				implementation = (
					<GlobalQuestionsListMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'contributors':
				implementation = expValEquals('cc_rewrite_contributors_legacy_macro', 'isEnabled', true) ? (
					<ContributorsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'widget':
				implementation = expValEquals('cc_rewrite_widget_legacy_macro', 'isEnabled', true) ? (
					<WidgetMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'contentbylabel':
				implementation = expValEquals(
					'cc_rewrite_contentbylabel_legacy_macro',
					'isEnabled',
					true,
				) ? (
					<ContentByLabelMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'pagetreesearch':
				implementation = expValEquals(
					'cc_rewrite_pagetree_search_legacy_macro',
					'isEnabled',
					true,
				) ? (
					<PageTreeSearchMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'content-report-table':
				implementation = expValEquals('cc_rewrite_content-report-table', 'isEnabled', true) ? (
					<ContentReportTableMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			// generic macro renderer
			default:
				implementation = legacyMacroRenderer;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(EDITOR, extension)}
					contentId={contentId}
					mode={EDITOR}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				{implementation}
			</Fragment>
		);
	});
